// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import DeleteIcon from '@mui/icons-material/Delete';

// material ui
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

/**
 * UnitPairDialog - Dialog for managing unit pairs
 *
 * Props:
 * - open: Boolean
 * - onClose: Function
 * - existingPairs: Array of { pairTag, flowUnit, powerUnit }
 * - availableFlowUnits: Array of flow units (filtered by dataType)
 * - availablePowerUnits: Array of power units (filtered by dataType)
 * - onCreatePair: Function({ flowUnitId, powerUnitId, pairTag }) => Promise
 * - onDeletePair: Function(pairTag, flowUnit, powerUnit) => Promise
 */
const UnitPairDialog = ({
  open,
  onClose,
  existingPairs = [],
  availableFlowUnits = [],
  availablePowerUnits = [],
  onCreatePair,
  onDeletePair,
}) => {
  const { t } = useTranslation('dashboards-compressed-air-basic');

  const [flowUnitId, setFlowUnitId] = useState('');
  const [powerUnitId, setPowerUnitId] = useState('');
  const [pairTag, setPairTag] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReset = () => {
    setFlowUnitId('');
    setPowerUnitId('');
    setPairTag('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleCreate = async () => {
    if (!flowUnitId || !powerUnitId || !pairTag.trim()) {
      return;
    }

    // Validate: check if pair tag already exists
    const tagExists = existingPairs.some((pair) => pair.pairTag === pairTag);
    if (tagExists) {
      // TODO: Show error snackbar - pair tag already exists
      console.error('Pair tag already exists');
      return;
    }

    // Validate: prevent pairing same unit twice
    if (flowUnitId === powerUnitId) {
      // TODO: Show error snackbar - cannot pair same unit
      console.error('Cannot pair unit with itself');
      return;
    }

    setIsSubmitting(true);
    try {
      await onCreatePair({
        flowUnitId: Number(flowUnitId),
        powerUnitId: Number(powerUnitId),
        pairTag: pairTag.trim(),
      });
      handleReset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (pair) => {
    setIsSubmitting(true);
    try {
      await onDeletePair(pair.pairTag, pair.flowUnit, pair.powerUnit);
    } finally {
      setIsSubmitting(false);
    }
  };

  const uniqueAvailableFlowUnits = availableFlowUnits.filter(
    (fu) =>
      !existingPairs.some((pair) => pair.flowUnit?.UnitId === fu.UnitId) &&
      !existingPairs.some((pair) => pair.powerUnit?.UnitId === fu.UnitId),
  );

  const uniqueAvailablePowerUnits = availablePowerUnits.filter(
    (pu) =>
      !existingPairs.some((pair) => pair.flowUnit?.UnitId === pu.UnitId) &&
      !existingPairs.some((pair) => pair.powerUnit?.UnitId === pu.UnitId),
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('unitPairDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Existing Pairs List */}
          {existingPairs.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('unitPairDialog.existingPairs')}
              </Typography>
              <List dense>
                {existingPairs.map((pair) => (
                  <ListItem
                    key={pair.pairTag}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleDelete(pair)}
                        disabled={isSubmitting}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={`${pair.pairTag}`}
                      secondary={`${pair.flowUnit?.Name || 'Unknown'} ↔ ${pair.powerUnit?.Name || 'Unknown'}`}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 2 }} />
            </Box>
          )}

          {/* Create New Pair */}
          <Typography variant="subtitle2">
            {t('unitPairDialog.createNew')}
          </Typography>

          {/* Alert if no available units */}
          {availableFlowUnits.length === 0 && (
            <Typography color="error">
              {t('unitPairDialog.noAvailableFlowUnits')}
            </Typography>
          )}

          <TextField
            select
            label={t('unitPairDialog.flowUnit')}
            value={flowUnitId}
            onChange={(e) => setFlowUnitId(e.target.value)}
            fullWidth
            disabled={isSubmitting}
          >
            <MenuItem value="">
              <em>{t('unitPairDialog.selectUnit')}</em>
            </MenuItem>
            {uniqueAvailableFlowUnits.map((unit) => (
              <MenuItem key={unit.UnitId} value={unit.UnitId}>
                {unit.Name}
              </MenuItem>
            ))}
          </TextField>

          {availablePowerUnits.length === 0 && (
            <Typography color="error">
              {t('unitPairDialog.noAvailablePowerUnits')}
            </Typography>
          )}

          <TextField
            select
            label={t('unitPairDialog.powerUnit')}
            value={powerUnitId}
            onChange={(e) => setPowerUnitId(e.target.value)}
            fullWidth
            disabled={isSubmitting}
          >
            <MenuItem value="">
              <em>{t('unitPairDialog.selectUnit')}</em>
            </MenuItem>
            {uniqueAvailablePowerUnits.map((unit) => (
              <MenuItem key={unit.UnitId} value={unit.UnitId}>
                {unit.Name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={t('unitPairDialog.pairTag')}
            value={pairTag}
            onChange={(e) => setPairTag(e.target.value)}
            fullWidth
            disabled={isSubmitting}
            placeholder={t('unitPairDialog.pairTagPlaceholder')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {t('unitPairDialog.cancel')}
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={
            !flowUnitId || !powerUnitId || !pairTag.trim() || isSubmitting
          }
        >
          {t('unitPairDialog.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnitPairDialog;
